import { Link } from 'react-router-dom';
import {
  formatInterest,
  interestStatusLabel,
} from 'pages/division-orders/structuring/interest-utils';
import type { InterestOverviewResponse } from 'pages/division-orders/structuring/types-interest';
import { useCardGet } from './use-card-resource';
import { DASH, ExpandAccordion, ExpandState, ExpandTable } from './common';

// DO interest lines (GET /documents/:id/division_order_structuring/interests).
// Reuses the canonical InterestRow contract; the card renders the mock's subset
// of columns plus the DOLI line-item identifier.
export const DoExpand = ({
  documentId,
  openSignal,
}: {
  documentId: number | string;
  // Broadcast from the card's Expand-All / Collapse-All control.
  openSignal?: { open: boolean };
}) => {
  const url = `/documents/${documentId}/division_order_structuring/interests`;
  const { loading, data, error } = useCardGet<InterestOverviewResponse>(url);

  if (loading) return <ExpandState>Loading…</ExpandState>;
  if (error) return <ExpandState>Failed to load line items.</ExpandState>;

  const rows = data?.interests ?? [];
  if (rows.length === 0) return <ExpandState>No line items.</ExpandState>;

  type Row = (typeof rows)[number];

  return (
    <ExpandAccordion
      title={`Line Items (${rows.length})`}
      openSignal={openSignal}
    >
      <ExpandTable<Row>
        bordered
        columns={[
          { header: 'Line Item', cell: (row) => row.name ?? DASH },
          { header: 'Property Name', cell: (row) => row.propertyName ?? DASH },
          {
            // Canonical Well match links to /wells/:uwi (the DO catalog target);
            // an imported-well match has no page, so its API/name shows as text.
            header: 'Well UWI/API',
            cell: (row) => {
              return row.uwi ? (
                <Link to={`/wells/${row.uwi}`}>{row.uwi}</Link>
              ) : (
                (row.importedWell?.apiNumber ??
                  row.importedWell?.wellName ??
                  DASH)
              );
            },
          },
          { header: 'Property ID', cell: (row) => row.propertyNumber ?? DASH },
          { header: 'Interest Type', cell: (row) => row.interestType ?? DASH },
          {
            header: 'Interest',
            align: 'right',
            cell: (row) => formatInterest(row.interest),
          },
          {
            header: 'Status',
            cell: (row) => interestStatusLabel(row.status),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.id}
        pageSize={25}
      />
    </ExpandAccordion>
  );
};
